import { anneeDe } from './cloture';
import { dateSuisse } from './dates';
import { paiementsDansLesRemarques, paiementsDe } from './paiements-ecrits';
import { auCentime, compteDansLeCa, htDocument, ttcDocument } from './vat';

/**
 * Les acomptes reçus une année avant leur facture.
 *
 * <p>La cliente verse 7'807.– le 24 décembre 2025 ; les travaux se font en
 * 2026 et la facture, datée de 2026, mentionne l'acompte dans ses remarques.
 * La comptabilité comptait tout en 2026, et la fiduciaire devait corriger le
 * bilan 2025 à la main. Les paiements de la facture sont maintenant inscrits à
 * leur date (« Paiements reçus »), et la société choisit, comme pour une
 * facture d'acompte :
 * <ul>
 *   <li>« encaissé » : l'acompte compte dans l'année où l'argent est arrivé ;
 *       la facture garde le reste ;</li>
 *   <li>« travaux » : tout compte dans l'année de la facture ; à la fin de
 *       l'année du versement, l'acompte figure au bilan comme acompte reçu
 *       d'avance.</li>
 * </ul>
 * Le document envoyé au client ne change pas : seule la comptabilité range
 * les montants.
 */

// Lus ici depuis toujours : les appelants continuent de les trouver.
export { paiementsDansLesRemarques, paiementsDe };

/** Clé du réglage de la société. */
export const CLE_REGLE_ACOMPTES = 'acomptesAnterieurs';

/** Les deux règles admises. */
export const REGLES_ACOMPTES = ['encaisse', 'travaux'];

/**
 * La règle retenue par la société ; « encaissé » par défaut, comme les
 * factures d'acompte.
 *
 * @param {object|null} reglage valeur du réglage
 * @returns {'encaisse'|'travaux'} la règle
 */
export function regleAcomptes(reglage) {
  return reglage?.regle === 'travaux' ? 'travaux' : 'encaisse';
}

/**
 * La règle de chaque année de versement : le patron choisit, année par année,
 * où compter les acomptes reçus — « ceux de 2025, je les ai déclarés en
 * 2025 ; ceux reçus en 2026 pour 2027, je déciderai peut-être 2027 ».
 * Sans choix pour une année, la règle générale de la société.
 *
 * @param {{regle?: string, parAnnee?: Object<string, string>}|null} reglage valeur du réglage
 * @returns {(annee: number) => 'encaisse'|'travaux'} la règle d'une année de versement
 */
export function reglesAcomptes(reglage) {
  const generale = regleAcomptes(reglage);
  return (annee) => {
    const choix = reglage?.parAnnee?.[annee];
    return choix === 'travaux' || choix === 'encaisse' ? choix : generale;
  };
}

/**
 * Le réglage après le choix d'une année de versement.
 *
 * @param {{regle?: string, parAnnee?: Object<string, string>}|null} reglage valeur du réglage
 * @param {number} annee année de versement
 * @param {'encaisse'|'travaux'} regle règle choisie
 * @returns {{regle: string, parAnnee: Object<string, string>}} le nouveau réglage
 */
export function avecRegleDeLAnnee(reglage, annee, regle) {
  return {
    regle: regleAcomptes(reglage),
    parAnnee: { ...(reglage?.parAnnee || {}), [annee]: regle },
  };
}

/**
 * Les paiements de chaque facture, à leur date suisse.
 *
 * @param {{factureId: number, date: string, montant: number}[]} encaissements paiements du serveur
 * @returns {Map<number, {date: string, montant: number}[]>} par facture, dans l'ordre
 */
export function paiementsParFacture(encaissements) {
  const parFacture = new Map();
  (encaissements || []).forEach((e) => {
    const liste = parFacture.get(e.factureId) || [];
    liste.push({ date: dateSuisse(e.date), montant: +e.montant || 0 });
    parFacture.set(e.factureId, liste);
  });
  return parFacture;
}

/** Les paiements d'une facture reçus avant l'année de la facture. */
function avantLaFacture(facture, paiements) {
  const annee = anneeDe(facture);
  return paiementsDe(facture, paiements).filter(
    (p) => p.montant > 0 && anneeDe({ date: p.date }) < annee,
  );
}

/** Une copie de la facture dont chaque ligne est réduite à une part. */
function part(facture, fraction, champs) {
  return {
    ...facture,
    ...champs,
    lignes: (facture.lignes || []).map((l) => ({ ...l, q: (+l.q || 0) * fraction })),
  };
}

/**
 * Les pièces que la comptabilité range par année : les factures, et pour
 * chaque acompte reçu une année avant sa facture, une part « acompte » datée
 * du versement, la facture gardant le reste.
 *
 * @param {object[]} factures factures de la société
 * @param {Map<number, {date: string, montant: number}[]>} paiements paiements par facture
 * @param {'encaisse'|'travaux'|((annee: number) => string)} regle règle de la
 *   société, ou celle de chaque année de versement (reglesAcomptes)
 * @returns {object[]} les pièces comptables ; les parts portent `partDe`
 */
export function piecesComptables(factures, paiements, regle) {
  const regleDe = typeof regle === 'function' ? regle : () => regle;
  if (typeof regle !== 'function' && regle !== 'encaisse') return factures || [];
  return (factures || []).flatMap((f) => {
    if (!compteDansLeCa(f) || f.type === 'avoir') return [f];
    // Seuls les acomptes d'une année comptée « au versement » se séparent.
    const avant = avantLaFacture(f, paiements?.get(f.id)).filter(
      (p) => regleDe(anneeDe({ date: p.date })) === 'encaisse',
    );
    const ttc = ttcDocument(f);
    if (!avant.length || !(ttc > 0)) return [f];
    const recu = Math.min(
      ttc,
      avant.reduce((s, p) => s + p.montant, 0),
    );
    const parts = avant.map((p, i) =>
      part(f, Math.min(1, p.montant / ttc), {
        id: f.id + '-acompte-' + i,
        partDe: f.id,
        type: 'acompte',
        num: (f.num || f.id) + ' · acompte',
        date: p.date,
        dateCompta: null,
        statut: 'Payée',
        paidAmount: p.montant,
        paidDate: p.date,
      }),
    );
    const reste = part(f, Math.max(0, 1 - recu / ttc), {
      paidAmount: Math.max(0, auCentime((+f.paidAmount || 0) - recu)),
    });
    return [...parts, reste];
  });
}

/**
 * Les acomptes reçus pendant une année pour des factures des années
 * suivantes, hors taxe : au bilan de cette année, un acompte reçu d'avance
 * quand la société compte l'année des travaux.
 *
 * @param {object[]} factures factures de la société
 * @param {Map<number, {date: string, montant: number}[]>} paiements paiements par facture
 * @param {number} annee exercice
 * @returns {number} le hors-taxe reçu d'avance
 */
export function acomptesRecusDAvance(factures, paiements, annee) {
  return auCentime(
    (factures || []).reduce((s, f) => {
      if (!compteDansLeCa(f) || f.type === 'avoir' || anneeDe(f) <= annee) return s;
      const ttc = ttcDocument(f);
      if (!(ttc > 0)) return s;
      const recus = paiementsDe(f, paiements?.get(f.id))
        .filter((p) => anneeDe({ date: p.date }) === annee)
        .reduce((t, p) => t + p.montant, 0);
      return s + (htDocument(f) * Math.min(ttc, recus)) / ttc;
    }, 0),
  );
}

/**
 * Les factures qui ont reçu un paiement une année avant leur date.
 *
 * @param {object[]} factures factures de la société
 * @param {Map<number, {date: string, montant: number}[]>} paiements paiements par facture
 * @returns {{facture: object, avant: {date: string, montant: number}[]}[]} les factures concernées
 */
export function facturesAvecAcompteAnterieur(factures, paiements) {
  return (factures || [])
    .filter((f) => compteDansLeCa(f) && f.type !== 'avoir')
    .map((f) => ({ facture: f, avant: avantLaFacture(f, paiements?.get(f.id)) }))
    .filter((x) => x.avant.length > 0);
}

/**
 * Le message de la facture sans les phrases des paiements qu'il annonce.
 *
 * <p>« Un acompte de CHF 7'807.– a été versé le 24.12.2025. » : inscrit dans
 * les paiements reçus, l'acompte figure sous le total du document ; la phrase
 * ne se répète plus tout en bas. Les autres phrases restent.
 *
 * @param {string} texte message imprimé en bas de la facture
 * @returns {string} le message sans les phrases de paiement
 */
export function sansLesPaiements(texte) {
  return String(texte || '')
    .split('\n')
    .map((ligne) =>
      ligne
        .split(/(?<=[.!?])\s+/)
        .filter((phrase) => !paiementsDansLesRemarques(phrase).length)
        .join(' ')
        .trim(),
    )
    .filter(Boolean)
    .join('\n');
}
